import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogIn, ShieldCheck, HardHat } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { BrandLockup } from "../components/Brand";
import dict from "../i18n/es-MX";

const DEMO_ADMIN = { email: "admin@demo.mx", password: "demo1234" };
const DEMO_OPERATOR = { email: "operador@demo.mx", password: "demo1234" };

function destinationFor(role) {
    if (role === "operator") return "/campo";
    return "/panel";
}

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    // null = still loading /api/app-config → hide the demo section entirely
    // to avoid a "demo buttons flash" on production before config resolves.
    const [appMode, setAppMode] = useState(null);

    useEffect(() => {
        api.get("/app-config")
            .then(r => setAppMode(r.data?.app_mode || "demo"))
            .catch(() => setAppMode("production"));   // fail closed
    }, []);

    const doLogin = async (creds) => {
        setError("");
        setFieldErrors({});
        setSubmitting(true);
        try {
            const user = await login(creds.email, creds.password);
            navigate(destinationFor(user.role), { replace: true });
        } catch (err) {
            setError(formatApiError(err) || dict.login.invalid);
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const fe = {};
        const cleanEmail = email.trim();
        if (!cleanEmail) fe.email = dict.validation.emailRequired;
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) fe.email = dict.validation.emailInvalid;
        if (!password) fe.password = dict.validation.passwordRequired;
        setFieldErrors(fe);
        if (Object.keys(fe).length > 0) return;
        doLogin({ email: cleanEmail, password });
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
            {/* Industrial header band */}
            <div
                className="h-2 w-full"
                style={{
                    background:
                        "repeating-linear-gradient(135deg, #F97316 0 18px, #0f172a 18px 36px)",
                }}
            />

            <div className="flex-1 flex items-center justify-center px-5 py-10">
                <div className="w-full max-w-md">
                    <div className="mb-10">
                        <BrandLockup size={48} showTagline />
                    </div>

                    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 backdrop-blur p-7 shadow-xl">
                        <h1 className="font-display text-3xl font-bold tracking-tight">
                            {dict.login.title}
                        </h1>
                        <p className="mt-1 text-sm text-slate-400">{dict.login.subtitle}</p>

                        <form className="mt-7 space-y-5" onSubmit={onSubmit} noValidate data-testid="login-form">
                            <div>
                                <label
                                    htmlFor="email"
                                    className="block text-xs uppercase tracking-wider text-slate-400 mb-2"
                                >
                                    {dict.login.email}
                                </label>
                                <input
                                    id="email"
                                    data-testid="login-email-input"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={dict.login.emailPlaceholder}
                                    aria-invalid={!!fieldErrors.email}
                                    className={`w-full h-12 px-4 rounded-lg bg-slate-950 border ${fieldErrors.email?"border-red-500":"border-slate-700"} focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/40 outline-none transition text-base`}
                                />
                                {fieldErrors.email && (
                                    <div className="field-error text-xs text-red-300 mt-1" data-testid="field-error">{fieldErrors.email}</div>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-xs uppercase tracking-wider text-slate-400 mb-2"
                                >
                                    {dict.login.password}
                                </label>
                                <input
                                    id="password"
                                    data-testid="login-password-input"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={dict.login.passwordPlaceholder}
                                    aria-invalid={!!fieldErrors.password}
                                    className={`w-full h-12 px-4 rounded-lg bg-slate-950 border ${fieldErrors.password?"border-red-500":"border-slate-700"} focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/40 outline-none transition text-base`}
                                />
                                {fieldErrors.password && (
                                    <div className="field-error text-xs text-red-300 mt-1" data-testid="field-error">{fieldErrors.password}</div>
                                )}
                            </div>

                            {error && (
                                <div
                                    data-testid="login-error"
                                    className="text-sm text-red-300 bg-red-900/40 border border-red-800 rounded-md px-3 py-2"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="login-submit-button"
                                className="w-full h-12 rounded-lg bg-brand-orange hover:bg-orange-400 active:bg-orange-600 text-slate-950 font-semibold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition disabled:opacity-60"
                            >
                                {submitting ? (
                                    <Loader2 className="animate-spin" size={18} />
                                ) : (
                                    <LogIn size={18} />
                                )}
                                {submitting ? dict.login.submitting : dict.login.submit}
                            </button>
                        </form>

                        <div className="mt-4 text-center">
                            <Link to="/forgot-password" data-testid="forgot-password-link"
                                className="text-xs text-slate-400 hover:text-brand-orange underline underline-offset-2">
                                ¿Olvidaste tu contraseña?
                            </Link>
                        </div>

                        {appMode === "demo" && (
                        <div className="mt-7 pt-6 border-t border-slate-800" data-testid="demo-section">
                            <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
                                {dict.login.demoSection}
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                                <button
                                    type="button"
                                    onClick={() => doLogin(DEMO_ADMIN)}
                                    disabled={submitting}
                                    data-testid="demo-admin-button"
                                    className="h-12 rounded-lg border border-slate-700 hover:border-brand-orange hover:bg-slate-800 text-slate-100 text-sm font-medium flex items-center justify-center gap-2 transition disabled:opacity-60"
                                >
                                    <ShieldCheck size={18} className="text-brand-orange" />
                                    {dict.login.demoAdmin}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => doLogin(DEMO_OPERATOR)}
                                    disabled={submitting}
                                    data-testid="demo-operator-button"
                                    className="h-12 rounded-lg border border-slate-700 hover:border-brand-orange hover:bg-slate-800 text-slate-100 text-sm font-medium flex items-center justify-center gap-2 transition disabled:opacity-60"
                                >
                                    <HardHat size={18} className="text-brand-orange" />
                                    {dict.login.demoOperator}
                                </button>
                            </div>
                        </div>
                        )}
                    </div>

                    <p className="mt-6 text-center text-xs text-slate-500">
                        © {new Date().getFullYear()} {dict.brand.name}
                    </p>
                </div>
            </div>
        </div>
    );
}
